/*
 * Bel Furniture accessibility patch for the Tokuhn / Guardian warranty modal.
 *
 * Two defects in Tokuhn's injected markup are patched here:
 *
 * 1. The decline control (#ID_TOK_WTY_CUST_DECLINE_TEXT) is a <p>, so it is
 *    never in the tab order. Keyboard users cannot decline the plan.
 *
 * 2. The modal container (#ID_TOK_WTY_CUST_CONTAINER_MODALS) is rendered near
 *    the top of the product section, earlier in the DOM than the widget that
 *    opens it. Tokuhn does not move focus on open, so pressing Tab walks
 *    forward through the rest of the page and never reaches the modal.
 *
 * This script makes the decline control operable, moves focus into the modal
 * when it opens, keeps focus inside while it is open, and restores focus to
 * the opener on close.
 *
 * This is a workaround for a third-party widget. Re-verify after any Tokuhn
 * update; the IDs and class prefix below are theirs, not ours.
 */
(() => {
  if (window.__belTokuhnA11yInstalled) return;
  window.__belTokuhnA11yInstalled = true;

  const MODAL_CONTAINER_ID = 'ID_TOK_WTY_CUST_CONTAINER_MODALS';
  const DECLINE_ID = 'ID_TOK_WTY_CUST_DECLINE_TEXT';
  const PROTECT_ID = 'ID_TOK_WTY_CUST_PROTECT_BUTTON';
  const ACTION_CONTAINER_ID = 'ID_TOK_WTY_CUST_ACTION_CONTAINER';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  let lastOpener = null;
  let isOpen = false;

  const container = () => document.getElementById(MODAL_CONTAINER_ID);

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement) || !element.isConnected) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return element.getClientRects().length > 0;
  };

  /*
   * The modal is considered open when the container holds anything visible.
   * Tokuhn toggles an overlay (.tokwtycust_overlay) plus panel markup rather
   * than exposing a state attribute we can read directly.
   */
  const modalIsOpen = () => {
    const root = container();
    if (!root) return false;

    const overlay = document.querySelector('.tokwtycust_overlay');
    if (overlay && isVisible(overlay)) return true;

    return Array.from(root.children).some(isVisible);
  };

  const focusableInModal = () => {
    const root = container();
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
  };

  /* ---- 1. Make the decline paragraph operable ------------------------- */

  const activateDecline = (element) => {
    /*
     * Tokuhn binds its own click handler to the <p>. Dispatching a real mouse
     * event is more reliable than .click() for third-party widget code.
     */
    element.dispatchEvent(new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      composed: true,
      view: window
    }));
  };

  const enhanceDecline = () => {
    const decline = document.getElementById(DECLINE_ID);
    if (!decline || decline.dataset.belA11y === 'true') return;

    decline.dataset.belA11y = 'true';
    decline.setAttribute('tabindex', '0');
    decline.setAttribute('role', 'button');
    decline.style.cursor = 'pointer';

    decline.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
        return;
      }
      event.preventDefault();
      activateDecline(decline);
    });
  };

  /*
   * Any other clickable-but-unfocusable elements Tokuhn renders (plan cards,
   * "Details & FAQ", etc). Conservative: only elements that already carry a
   * pointer cursor or an inline click handler are touched.
   */
  const enhanceOtherControls = () => {
    const root = container();
    if (!root) return;

    root.querySelectorAll('[class*="tokwtycust"]').forEach((element) => {
      if (element.dataset.belA11y === 'true') return;
      if (element.matches(FOCUSABLE_SELECTOR)) return;
      if (element.id === DECLINE_ID) return;

      const clickable =
        element.hasAttribute('onclick') ||
        window.getComputedStyle(element).cursor === 'pointer';

      if (!clickable) return;
      /* Skip wrappers: only leaf-ish controls should become tab stops. */
      if (element.querySelector(FOCUSABLE_SELECTOR)) return;

      element.dataset.belA11y = 'true';
      element.setAttribute('tabindex', '0');
      if (!element.getAttribute('role')) {
        element.setAttribute('role', 'button');
      }

      element.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
          return;
        }
        event.preventDefault();
        element.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          composed: true,
          view: window
        }));
      });
    });
  };

  /* ---- 2. Focus management -------------------------------------------- */

  const markModalSemantics = () => {
    const root = container();
    if (!root || root.dataset.belA11yDialog === 'true') return;

    root.dataset.belA11yDialog = 'true';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Protection plan options');
  };

  const onOpen = () => {
    isOpen = true;
    markModalSemantics();
    enhanceDecline();
    enhanceOtherControls();

    /*
     * Focus the primary action so the modal is immediately reachable, rather
     * than leaving focus behind in the product form where Tab would never
     * walk backwards to find it.
     */
    window.requestAnimationFrame(() => {
      const protectBtn = document.getElementById(PROTECT_ID);
      const target = (protectBtn && isVisible(protectBtn))
        ? protectBtn
        : focusableInModal()[0];

      if (target) target.focus({ preventScroll: true });
    });
  };

  const onClose = () => {
    isOpen = false;

    if (lastOpener && lastOpener.isConnected && isVisible(lastOpener)) {
      lastOpener.focus({ preventScroll: true });
    }
    lastOpener = null;
  };

  /* Keep focus inside the modal while it is open. */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !isOpen) return;

    const stops = focusableInModal();
    if (stops.length === 0) return;

    const first = stops[0];
    const last = stops[stops.length - 1];
    const active = document.activeElement;
    const root = container();

    /* Focus escaped the modal entirely: pull it back in. */
    if (!root || !root.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    }
  }, true);

  /* Remember what opened the modal so focus can be restored on close. */
  document.addEventListener('click', (event) => {
    if (isOpen) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (container()?.contains(target)) return;

    const focusableOpener = target.closest(FOCUSABLE_SELECTOR)
      || (target instanceof HTMLElement ? target : null);

    if (focusableOpener) lastOpener = focusableOpener;
  }, true);

  /* ---- Watch for open/close ------------------------------------------- */

  const check = () => {
    const openNow = modalIsOpen();
    if (openNow && !isOpen) {
      onOpen();
    } else if (!openNow && isOpen) {
      onClose();
    } else if (openNow) {
      /* Tokuhn re-renders panel contents between steps. */
      enhanceDecline();
      enhanceOtherControls();
    }
  };

  const observer = new MutationObserver(check);

  const start = () => {
    if (!document.body) return;
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
    check();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
